/**
 * Park Home Support — long-form SEO content registry.
 *
 * Each entry can be rendered via <SeoArticle {...content} />.
 * Adding new SEO pages = adding new keys here + a route + a sitemap line.
 *
 * Content is written deliberately to be:
 *   - factually grounded (no invented stats / fake reviews)
 *   - long enough for Google to treat seriously (~700-1200 words)
 *   - useful enough that residents actually read it
 *   - cross-linked back into the conversion funnel
 */

// ─── COMPETITOR COMPARISON PAGES ────────────────────────────────────────────

export const PARK_HOME_ASSIST_ALTERNATIVE = {
  title: "Park Home Assist Alternative | Independent Park Home Support",
  description:
    "Looking for an alternative to Park Home Assist? Compare independent quote review, inspection and insurance guidance — without the contractor or insurer conflict of interest.",
  keywords: "park home assist alternative, park home assist vs park home support, independent park home advice",
  canonical: "https://parkhomesupport.co.uk/park-home-assist-alternative",
  eyebrow: "Alternatives",
  h1: "Park Home Assist Alternative — Independent Support, Not Insurance-Linked.",
  intro:
    "If you've been quoted by Park Home Assist or you're weighing up your options, this page explains how independent park home support differs from policy-led services — and why that distinction matters when work is about to start on your home.",
  sections: [
    {
      h2: "Where Park Home Assist sits in the market",
      body:
        "Park Home Assist is a long-standing specialist insurer covering park homes. They offer well-known buildings, contents and bespoke residential park home policies. Their strength is their underwriting expertise and claims-handling experience inside the park home niche.\n\nWhere insurance-led services have a structural limitation is in what they're set up to do. An insurer is incentivised — quite properly — to manage claims efficiently. That means appointed contractors, scope set by loss adjusters, and a process geared around the claim itself rather than the long-term wellbeing of the home.",
    },
    {
      h2: "How Park Home Support is different",
      body:
        "Park Home Support is not an insurer, not a contractor, and not paid by any third party. We exist to give park home owners independent technical and procedural guidance before money is committed.\n\nWhen residents come to us, the question is rarely 'will my insurance cover this?' — it's 'is this work necessary, is the scope correct, and is the price reasonable?' Those are the questions you can't ask the people quoting for the work or the people paying for it.",
      bullets: [
        "Independent quote review before you approve any work",
        "Plain-English guidance on whether an issue is a maintenance matter or a claim",
        "Help understanding contractor scopes, prices and trading practices",
        "Optional independent inspection where the situation needs it",
        "Ongoing membership support that doesn't depend on insurance status",
      ],
    },
    {
      h2: "When you'd use both",
      body:
        "You don't have to choose. A common pattern: you keep specialist insurance with Park Home Assist (or a similar provider), and you keep Park Home Support membership for the technical/procedural side — quote review, inspection guidance, and an independent voice when something doesn't feel right.\n\nThe two complement each other. Insurance covers the financial outcome of insurable events. Independent support covers everything that happens between the issue arising and the work being done — the part that, when it goes wrong, costs the most.",
    },
    {
      h2: "Who Park Home Support is for",
      body:
        "If you've been given a quote you're unsure about, told work needs to start urgently, or quoted on a base, roof, damp or insurance-related repair and you want a second pair of eyes before you sign — we're built for exactly that moment. Quote review starts before membership; you don't need to join to ask.",
    },
  ],
  faq: [
    {
      q: "Is Park Home Support an insurance company?",
      a: "No. Park Home Support provides independent technical and procedural guidance. We aren't authorised by the FCA, we don't issue policies and we don't sell insurance. If you need insurance cover, you'd hold that with a specialist insurer; we sit alongside that and help with the decisions and quotes.",
    },
    {
      q: "Can I use Park Home Support if I'm already insured with Park Home Assist?",
      a: "Yes. The two are complementary. Many of our members hold specialist insurance separately and use Park Home Support for quote review, inspection guidance and independent input.",
    },
    {
      q: "How much does Park Home Support cost?",
      a: "Membership is £99 for the first year (then £249/year). Quote review and one-off enquiries can be sent in without joining — there's no obligation to upgrade.",
    },
    {
      q: "Will Park Home Support recommend a contractor?",
      a: "Where appropriate we can point owners towards reputable independent inspectors and qualified trades, but we don't take commission and we don't appoint contractors on your behalf — the decision and contract remain yours.",
    },
  ],
};

export const GOLDSHIELD_ALTERNATIVE = {
  title: "GoldShield Warranty Alternatives | Independent Park Home Support",
  description:
    "Looking for an alternative to GoldShield warranty? See how independent quote review and inspection support work differently — and where each one is the right tool.",
  keywords: "goldshield alternative, goldshield warranty alternative, park home warranty alternative",
  canonical: "https://parkhomesupport.co.uk/goldshield-alternative",
  eyebrow: "Alternatives",
  h1: "GoldShield Alternatives — Independent Support, Not a Warranty.",
  intro:
    "GoldShield is a well-known structural warranty in the park home market. This page is for owners who already hold (or have been offered) GoldShield cover and are wondering what 'independent support' adds on top — and where one stops and the other starts.",
  sections: [
    {
      h2: "What a structural warranty does",
      body:
        "GoldShield, like any structural warranty, covers specified defects in workmanship and materials for a defined period. It's underwritten cover, with policy wording that lists what's in and what's out. If your home falls within the scope and you have a valid claim, the warranty pays.\n\nWhat a warranty doesn't do is help you decide whether to approve a quote, judge whether scope is correct, or argue your corner with a contractor whose work has already been done.",
    },
    {
      h2: "What Park Home Support adds",
      body:
        "We operate before money is spent. The most expensive moments in a park home's life are when work is approved that didn't need to happen, when scope is bigger than necessary, when prices are inflated due to urgency, or when an insurable issue gets paid for privately because nobody flagged that it might be a claim.\n\nThat's the gap Park Home Support sits in. Independent review of quotes, independent guidance on inspection, independent input on insurance relevance — combined into one ongoing membership.",
    },
    {
      h2: "Using a warranty and Park Home Support together",
      body:
        "If you hold GoldShield (or any warranty), keep it. It's a financial safety net for the things it's specifically written to cover. Sitting Park Home Support alongside it means you have a procedural safety net — somebody to check the quote, sense-check the scope, and tell you whether to make the claim, get an inspection first, or push back on the contractor before anything is signed.",
    },
  ],
  faq: [
    {
      q: "Is Park Home Support a warranty?",
      a: "No. We're not a warranty, not a guarantee, and not insurance. We provide independent technical and procedural guidance to park home owners.",
    },
    {
      q: "Will Park Home Support help me make a warranty claim?",
      a: "We can review the situation, help you understand whether the issue is likely to fall within typical warranty wording, and guide you on what evidence to gather before contacting the warranty provider. The claim itself stays with the warranty provider.",
    },
    {
      q: "I'm being offered a warranty by a contractor — should I take it?",
      a: "Send the proposal in and we'll review it. Warranties vary enormously in wording, term, exclusions and price — what looks like cover can sometimes turn out to be very narrow once you read the small print.",
    },
    {
      q: "How quickly can I get a quote reviewed?",
      a: "Most quote reviews are returned within a few working days. If the situation is urgent, flag it on the form and we'll prioritise.",
    },
  ],
};

export const PARK_HOME_INSURANCE_ALTERNATIVES = {
  title: "Park Home Insurance Alternatives | Independent Support Compared",
  description:
    "Looking at park home insurance alternatives? Understand the difference between specialist insurers, warranty products and independent support — and what each actually covers.",
  keywords: "park home insurance alternatives, park home insurance comparison, independent park home support",
  canonical: "https://parkhomesupport.co.uk/park-home-insurance-alternatives",
  eyebrow: "Alternatives",
  h1: "Park Home Insurance Alternatives — What Each One Actually Covers.",
  intro:
    "If you're researching alternatives to park home insurance, it usually means one of three things: you've had a claim experience that didn't go the way you hoped, you've been quoted a renewal that feels high, or you're trying to understand what 'support' actually means when insurers, warranty providers and independent services all use similar language.",
  sections: [
    {
      h2: "Specialist park home insurance",
      body:
        "A handful of UK insurers underwrite park home policies properly — examples include Park Home Assist, Towergate, Adrian Flux and Just Park Homes. These are the right product for fire, flood, storm, escape of water, theft, accidental damage and similar insurable events. Premiums vary based on age of home, location, claim history and construction.\n\nThe limitation isn't the cover — it's that an insurance product can only ever do what the policy wording says. It won't help you decide whether to approve a quote, judge whether a contractor is being fair, or coordinate the procedural side of getting work properly done.",
    },
    {
      h2: "Structural warranties",
      body:
        "Products like GoldShield are written cover against specified defects, usually for newer homes or after specific remedial work. They're useful for the things they're designed for. They don't cover wear-and-tear, contractor disputes, scope challenges or general procedural support.",
    },
    {
      h2: "Independent support (what Park Home Support does)",
      body:
        "We sit outside the insurance / warranty world entirely. We don't pay claims, but we also don't have any financial reason to want the work to happen or not happen. That makes us useful at the exact moment most owners feel alone — when a quote has landed and there's nobody to ask whether it's fair.\n\nMembership is £99 for the first year (then £249/year) and covers: quote review, inspection guidance, insurance-relevance input, and ongoing support whenever something doesn't feel right.",
    },
    {
      h2: "Which combination is right for you?",
      body:
        "For most park home owners, the strongest setup is: specialist insurance for the policy-covered risks + Park Home Support for the day-to-day decisions and quote reviews. The insurance handles the financial outcome of insurable events; the support handles everything around them. They don't overlap and they don't compete.",
    },
  ],
  faq: [
    {
      q: "Can I cancel my park home insurance and just use Park Home Support?",
      a: "No — we don't replace insurance. If your home is uninsured and something happens (fire, storm, escape of water), you'd be exposed financially. Keep specialist insurance; use independent support alongside it.",
    },
    {
      q: "Will Park Home Support help me argue with my insurer?",
      a: "We can give independent input on whether a quote, scope or settlement looks reasonable, and we can help you frame what to ask. We don't act as a regulated claims management company — we don't handle the claim on your behalf.",
    },
    {
      q: "What if my insurance has been refused or my premium has been hiked?",
      a: "We see this regularly. Often it's worth getting a second quote from a different specialist before changing anything, and getting the underlying issue (e.g. base or roof condition) properly assessed so future cover is straightforward.",
    },
    {
      q: "Do you sell insurance?",
      a: "No. We aren't FCA-authorised and we don't issue policies or earn commission on policies.",
    },
  ],
};

export const BEST_PARK_HOME_SUPPORT_SERVICES = {
  title: "Best Park Home Support Services UK | What To Look For",
  description:
    "Comparing park home support services? Understand what each type actually offers, what to look for, and where Park Home Support fits in the picture.",
  keywords: "best park home support services uk, park home support comparison, park home help",
  canonical: "https://parkhomesupport.co.uk/best-park-home-support-services",
  eyebrow: "Compare Services",
  h1: "Best Park Home Support Services in the UK — What To Actually Look For.",
  intro:
    "There's no shortage of services aimed at park home owners — but they're built around very different business models, and that affects what you'll actually get when you need help. This page is a plain-English guide to the categories, what each does well, and what to look for before you sign anything.",
  sections: [
    {
      h2: "Category 1 — Specialist insurance",
      body:
        "Insurers like Park Home Assist, Towergate, Adrian Flux and Just Park Homes underwrite policies designed for park homes. Best for: covering insurable risks. Look for: clear claims handling, evidence of paying claims promptly, transparent excesses, no aggressive exclusions on age or construction.",
    },
    {
      h2: "Category 2 — Warranty products",
      body:
        "Structural warranties like GoldShield cover specified defects for a defined period. Best for: peace of mind on new builds or post-remediation. Look for: actual policy wording (not marketing), the exclusions list, the claim limits, and whether it follows the home on resale.",
    },
    {
      h2: "Category 3 — Independent inspections",
      body:
        "Independent inspectors with park-home-specific experience are different from generic surveyors. Best for: pre-purchase, pre-sale, dispute, insurance, or before approving major works. Look for: park home specialism (not just RICS), photographic evidence in the report, clear next-step recommendations, willingness to talk through findings.",
    },
    {
      h2: "Category 4 — Independent ongoing support (what we do)",
      body:
        "Park Home Support sits in this category. Best for: the decisions around quotes, contractors, insurance relevance, scope, urgency and whether to engage an inspector. Look for: no contractor commission, no insurance commission, clear membership pricing, transparent scope of service, ability to send in one-off questions before committing.",
    },
    {
      h2: "What good looks like across all of them",
      body:
        "Whichever services you end up using, the universal hallmarks of a credible park home service are: park-home-specific expertise (not generic property), transparent pricing, willingness to put advice in writing, and no pressure to sign on the spot. If anything is being rushed, that itself is a flag.",
    },
  ],
  faq: [
    {
      q: "Do I need all of these services?",
      a: "Most owners need insurance and benefit from independent support. Warranty depends on the age of the home and what work has been done. Inspections are situational — best taken when something specific is happening (purchase, sale, dispute, major repair).",
    },
    {
      q: "What's the single most useful service if I can only pick one?",
      a: "Independent support. It's the cheapest, broadest, and the one most likely to save money on the day you actually need help. Specialist insurance is essential for risk transfer but doesn't help you make day-to-day decisions.",
    },
    {
      q: "How do I check if a park home service is legitimate?",
      a: "Look for verifiable contact details, real address, transparent pricing, no aggressive sales tactics, and the ability to send in a question without paying first. If you can't speak to a human or get a clear written summary, walk away.",
    },
  ],
};

// ─── SERVICE PAGES ──────────────────────────────────────────────────────────

export const SERVICE_INSPECTION = {
  title: "Park Home Inspection | Independent Pre-Work & Pre-Purchase Inspections",
  description:
    "Independent park home inspections — base, roof, damp, structural and general condition. Plain-English reports, photographic evidence, recommended next steps.",
  keywords: "park home inspection, park home survey, independent park home inspection, park home base inspection",
  canonical: "https://parkhomesupport.co.uk/park-home-inspection",
  eyebrow: "Service",
  h1: "Park Home Inspection — Independent & Park-Home-Specific.",
  intro:
    "A park home inspection isn't the same as a house survey. Park homes have their own construction, their own failure patterns and their own regulatory context. Generic surveyors regularly miss what matters; the right inspection saves money before any work starts.",
  sections: [
    {
      h2: "When to get a park home inspection",
      body:
        "The four moments that pay back fastest:\n\n1. Before purchase — a proper park home inspection will tell you about the base, the chassis, the roof, the windows, the damp situation and the wider site context. Most surveys don't.\n2. Before sale — you find out what a buyer's surveyor would find, and decide what to fix and what to disclose.\n3. Before approving major work — if a contractor has quoted thousands of pounds of roof or base work, an independent inspection clarifies whether the work is necessary and at what scope.\n4. After an insurance event — independent inspection produces a documented record of the damage and the proposed scope that you can put in front of the insurer's loss adjuster.",
    },
    {
      h2: "What a good inspection actually covers",
      body:
        "A useful park home inspection looks at: the base (level, condition, drainage, evidence of movement), the chassis (corrosion, cross-bracing, wheel arches where relevant), the underside (insulation, vapour barrier, services penetrations), the exterior (cladding, joints, vents, weatherproofing), the roof (covering, junctions, fixings, flashings, gutters), the interior (damp evidence, soft spots, ceiling stains, internal movement), the services (gas certificate currency, electrical condition where visible) and the site context (skirting, drainage, vegetation, neighbour proximity).\n\nThe report you get back should include photographs of the issues, a clear severity rating, recommended actions and an indication of urgency.",
    },
    {
      h2: "What an inspection does not do",
      body:
        "It doesn't price the work. It doesn't appoint contractors. It doesn't replace insurance or a warranty. It's a snapshot in time of the condition of the home and the recommended actions — which is exactly what's needed to make good decisions afterwards.",
    },
    {
      h2: "Park Home Support member rates",
      body:
        "Members receive preferential inspection rates and priority access. You don't need to be a member to commission an inspection through us, but the membership pays for itself quickly if an inspection is in your near future.",
    },
  ],
  faq: [
    {
      q: "How much does a park home inspection cost?",
      a: "Costs vary by location and scope. Members of Park Home Support receive preferential rates. Send in your details and we'll come back with a clear quote — no obligation.",
    },
    {
      q: "How long does a park home inspection take?",
      a: "Most external + internal inspections take 2–4 hours on site, with the report following within a few working days.",
    },
    {
      q: "Do I have to be there?",
      a: "It's preferable for the internal portion, but external + base inspections can usually be done with access arrangements alone.",
    },
    {
      q: "Will the inspection report stand up against an insurer or a contractor?",
      a: "Yes. Independent, photographically evidenced reports carry weight in conversations with insurers, contractors and site operators. We word them carefully so they're useful in dispute.",
    },
  ],
};

export const SERVICE_REPAIRS = {
  title: "Park Home Repairs | Independent Quote Review & Repair Guidance",
  description:
    "Park home repairs — before you approve, get the quote independently reviewed. Roof, base, damp, structural and general repair guidance.",
  keywords: "park home repairs, park home repair costs, park home quote review",
  canonical: "https://parkhomesupport.co.uk/park-home-repairs",
  eyebrow: "Service",
  h1: "Park Home Repairs — Don't Approve Until It's Been Checked.",
  intro:
    "The most expensive park home repairs aren't the technically difficult ones — they're the ones where unnecessary work was approved, scope was inflated, or the wrong contractor was used. Independent quote review fixes that, before any money is committed.",
  sections: [
    {
      h2: "Where park home repairs typically go wrong",
      body:
        "The pattern is consistent: a contractor visits, gives a verbal price the same day, says the work needs to start soon. Anxiety does the rest.\n\nThe failures we see most are: scope creep (replacing what could be repaired), bundled pricing (so you can't tell what's reasonable), unnecessary urgency (real urgency exists but not as often as it's claimed), missing the insurance question entirely, and not capturing photographic evidence before work starts.\n\nEach of these is fixable. None of them are fixable once the work has started.",
    },
    {
      h2: "What independent quote review actually does",
      body:
        "We look at the written quote (or the verbal price written down), the description of works, any supporting photographs, and the wider context. We come back with a plain-English review: what's reasonable, what looks unclear or excessive, whether the scope is appropriate, and whether anything in the situation looks insurance-relevant. Where helpful, we recommend a proper independent inspection before any work goes ahead.",
    },
    {
      h2: "Common repair categories we review",
      body:
        "Roof repairs (covering, junctions, flashings), base repairs (level, condition, drainage), damp remediation (cause first, treatment second), insulation work, windows and doors, exterior cladding, structural repairs (chassis, cross-bracing), and the catch-all 'general refurbishment' quotes that often hide overlapping line items.",
      bullets: [
        "Roof tile / felt / fibreglass replacement and joint sealing",
        "Base relevelling and pad / pier rectification",
        "Damp diagnosis and remediation",
        "Window / door replacement (and where repair is sufficient)",
        "Underside insulation and vapour barriers",
        "Chassis rust treatment and structural reinforcement",
        "Driveways, skirting, drainage around the home",
      ],
    },
    {
      h2: "Cost of getting it wrong",
      body:
        "On a single roof replacement, the difference between a fair scope at fair price and a maximalist quote can easily be £4,000 — £8,000. On base work it can be more. The cost of a quote review is a fraction of any of that.",
    },
  ],
  faq: [
    {
      q: "How long does quote review take?",
      a: "Most reviews are returned within a few working days. If the situation is urgent, flag it on the form and we'll prioritise.",
    },
    {
      q: "What if I don't have a written quote yet?",
      a: "Send what you've got — even a verbal price written down, or photographs of the issue. We can advise on what to ask for in writing before you commit.",
    },
    {
      q: "Will Park Home Support refuse my quote on my behalf?",
      a: "We give you the analysis; the decision and the conversation with the contractor stay with you. We can help frame the conversation.",
    },
    {
      q: "Can I send pictures or video?",
      a: "Yes. Photos and short videos are often more useful than written descriptions. There's an upload field on the quote review form.",
    },
  ],
};

export const SERVICE_MAINTENANCE = {
  title: "Park Home Maintenance | Annual Checks & Preventative Guidance",
  description:
    "Park home maintenance done properly is preventative, not reactive. Annual checks, seasonal tasks and what to spot before it becomes expensive.",
  keywords: "park home maintenance, park home annual check, park home preventative maintenance",
  canonical: "https://parkhomesupport.co.uk/park-home-maintenance",
  eyebrow: "Service",
  h1: "Park Home Maintenance — Catch Small Things Before They Become Big.",
  intro:
    "Park homes reward consistent maintenance more than almost any other type of UK property. The materials, the construction and the exposure mean that catching things early is the difference between a small spend and a four-figure repair.",
  sections: [
    {
      h2: "The annual maintenance checklist",
      body:
        "Once a year, ideally in spring after winter exposure: roof and junctions inspected, gutters cleared, all external sealants checked, ventilation grilles cleared, base inspected for evidence of movement or pooling water, underside checked for rodent / insulation issues, all external woodwork inspected for paint/sealant failure, gas certificate currency confirmed, fire/CO alarms tested.",
      bullets: [
        "Roof covering, flashings, fixings",
        "Gutters and downpipes",
        "Window and door seals",
        "Cladding joints and sealants",
        "Underside insulation and vapour barrier",
        "Base level and drainage",
        "Skirting and ventilation",
        "Gas safety certificate currency",
        "Electrical condition (visible)",
        "Smoke and CO alarms",
      ],
    },
    {
      h2: "Seasonal tasks",
      body:
        "Autumn — clear gutters, check roof for any loose materials, test heating before the cold sets in. Winter — keep ventilation paths clear, monitor for any new damp or condensation, prevent freezing of exposed pipes. Spring — full external inspection after winter exposure, attend to anything that's deteriorated, plan any larger works for summer. Summer — book the larger work and the gas safety check.",
    },
    {
      h2: "What to spot early",
      body:
        "Park homes telegraph problems early if you know what to look for: doors not closing properly (often a base or chassis movement clue), faint staining on ceilings (a roof junction issue rather than a leaking pipe, usually), persistent condensation in one room (a ventilation or insulation issue), soft spots underfoot, peeling paint on external woodwork, vegetation touching the cladding.\n\nIf anything on that list rings a bell, send a photo through — we'll tell you whether it's something to monitor or something to act on.",
    },
  ],
  faq: [
    {
      q: "Do I need a professional to do annual maintenance?",
      a: "A confident DIY owner can do a lot of it. The annual gas safety check should be done by a qualified Gas Safe engineer. Anything you're not sure about, send a photo and ask.",
    },
    {
      q: "Is there a maintenance plan I can buy?",
      a: "We don't sell maintenance contracts because most owners don't need them. Membership gives you the ongoing 'ask anything' support — which usually covers what an annual plan would.",
    },
    {
      q: "How do I find a trustworthy maintenance contractor?",
      a: "Look for: park home specialism, willingness to put a scope in writing, fair fixed pricing rather than open-ended day rates, real local presence, and recent verifiable references.",
    },
  ],
};

export const SERVICE_INSURANCE_CLAIM = {
  title: "Park Home Insurance Claim Support | Independent Guidance",
  description:
    "Park home insurance claim help — independent guidance on scope, evidence and what to do before agreeing settlement. We don't handle claims; we help you make better decisions.",
  keywords: "park home insurance claim, park home insurance help, park home insurance claim rejected",
  canonical: "https://parkhomesupport.co.uk/park-home-insurance-claim-support",
  eyebrow: "Service",
  h1: "Park Home Insurance Claim Support — Independent Input Before You Agree.",
  intro:
    "Insurance claims for park homes can be straightforward — or they can be a months-long process of scope disagreements, loss adjuster visits and contractor disputes. Independent input doesn't replace your insurer, but it can dramatically improve what gets paid and what gets fixed.",
  sections: [
    {
      h2: "What to do in the first 48 hours",
      body:
        "1. Make it safe. Anything that's an active leak, escape of water, or safety hazard — stabilise (don't repair) without invalidating the claim.\n2. Photograph everything. Wide shots, close-ups, the cause if visible, the damage, the surrounding area. Time-stamped if your phone allows.\n3. Don't replace materials yet. Removing the damaged item destroys the evidence the loss adjuster needs.\n4. Notify your insurer. Brief, factual.\n5. Get independent input before agreeing any contractor scope.",
    },
    {
      h2: "Where claims go wrong",
      body:
        "Common failure modes: agreeing scope before evidence is properly captured, accepting an insurer-appointed contractor without question, signing off on settlement before related issues are identified, missing that one event has caused multiple insurable losses (e.g. storm damages roof, which causes interior damage, which causes carpet replacement — each can be a line in the claim).\n\nOn the other side: assuming everything is a claim. Some issues are wear-and-tear and won't be covered. Knowing the difference early saves a wasted process.",
    },
    {
      h2: "What independent support actually does here",
      body:
        "We review the situation, look at the proposed scope (whether yours or the insurer's contractor's), comment on whether it's complete and fair, and help you understand what to push back on. We don't handle the claim itself — we're not regulated to and we're not paid to.\n\nFor disputed or rejected claims, we can advise on whether an independent inspection would strengthen your position before you escalate to the Financial Ombudsman.",
    },
  ],
  faq: [
    {
      q: "My park home insurance claim has been rejected — what now?",
      a: "Don't accept the rejection without reading the reasons carefully. Often the reason is technically arguable. Get the situation reviewed before responding, and consider whether an independent inspection report would strengthen your appeal.",
    },
    {
      q: "Can Park Home Support handle my insurance claim?",
      a: "No — claims management is a regulated activity and we aren't FCA-authorised for it. We can give independent input on the technical and procedural side, which is usually where claims go wrong.",
    },
    {
      q: "Should I use the insurer's appointed contractor or my own?",
      a: "Depends on the situation. Insurer-appointed contractors are often efficient on scope-defined work, but you may want your own contractor for more complex jobs. We can help you weigh up the trade-offs.",
    },
    {
      q: "How long should a park home insurance claim take?",
      a: "Simple claims: weeks. Complex claims with structural elements: months. The single biggest accelerator is having proper evidence captured early and a documented scope agreed quickly.",
    },
  ],
};

export const SERVICE_QUOTE_REVIEW = {
  title: "Park Home Quote Review | Independent Check Before You Approve",
  description:
    "Park home quote review — independent check on scope, cost and insurance relevance. Send your quote in, get a plain-English review before approving any work.",
  keywords: "park home quote review, park home quote check, park home repair quote",
  canonical: "https://parkhomesupport.co.uk/park-home-quote-review-service",
  eyebrow: "Service",
  h1: "Park Home Quote Review — Plain-English Check Before You Approve.",
  intro:
    "Quote review is the single highest-leverage service Park Home Support offers. A small, fast process before the work starts — and the difference, on a typical roof or base repair, is often thousands of pounds.",
  sections: [
    {
      h2: "What you send us",
      body:
        "The written quote (or verbal price written down), a description of the issue in your own words, any photographs you have of the problem, and the contractor's name if you're comfortable sharing it. The more we have, the more useful the review — but we can work with a minimal pack too.",
    },
    {
      h2: "What we look at",
      body:
        "Scope: is what's being proposed actually necessary, and is it complete or are things missing?\nCost: is the price within the reasonable range for the work and the region?\nInsurance relevance: is anything in the issue potentially covered, and what would need to happen before claiming?\nContractor: is the proposal in writing, with materials specified, warranty terms clear, and a fair payment structure?\nUrgency: is the timeline being driven by the genuine condition of the home, or by the contractor's schedule?",
    },
    {
      h2: "What you get back",
      body:
        "A written review covering each of the above. Where appropriate, suggested follow-up questions to send the contractor. Where the situation needs it, a recommendation to commission an independent inspection before any work starts. Where the issue might be insurable, a note on the procedural steps to take.",
    },
    {
      h2: "Why this works",
      body:
        "Park home contractors operate in a niche where most customers can't easily benchmark prices or judge scope. Independent quote review provides that benchmark. Reputable contractors don't mind a quote being reviewed — it's the ones who do mind that you most want to check.",
    },
  ],
  faq: [
    {
      q: "Do I have to be a member to send a quote in?",
      a: "No. Quote review is open to anyone. Membership unlocks ongoing support and other benefits, but it's not required for a one-off.",
    },
    {
      q: "How quickly will I get the review back?",
      a: "Most reviews come back within a few working days. Flag urgency on the form and we'll prioritise.",
    },
    {
      q: "What if my quote is fair?",
      a: "We'll tell you. A quote review confirming a fair quote is genuinely useful too — it lets you proceed with confidence.",
    },
    {
      q: "Will the contractor know I've had it reviewed?",
      a: "Not from us. We don't contact the contractor unless you explicitly ask us to.",
    },
  ],
};

// ─── LOCATION PAGES ─────────────────────────────────────────────────────────

const locationBody = (county, examples = "") => ({
  title: `Park Home Support in ${county} | Inspections, Repairs & Insurance Help`,
  description: `Independent park home support across ${county} — quote review, inspections, insurance guidance and repair advice. Plain-English help before you commit to costly work.`,
  keywords: `park home support ${county.toLowerCase()}, park home inspections ${county.toLowerCase()}, park home repairs ${county.toLowerCase()}, park home survey ${county.toLowerCase()}`,
  canonical: `https://parkhomesupport.co.uk/park-home-support-${county.toLowerCase()}`,
  eyebrow: county,
  h1: `Park Home Support in ${county} — Independent Help When You Need It.`,
  intro: `${county} has a significant park home population, with sites across the county hosting full-time residential and holiday home owners. Park Home Support provides independent quote review, inspection coordination, insurance guidance and ongoing membership support throughout ${county}.`,
  sections: [
    {
      h2: `Why ${county} park home owners use us`,
      body: `Owners in ${county} contact us for the same situations as elsewhere in the UK: a contractor has quoted thousands for roof or base work and they want a second opinion, a damp problem has appeared and they're not sure whether it's a maintenance issue or a claim, an insurer's response feels unclear, or they're considering a purchase and want a proper inspection before exchanging contracts.\n\nWe're not local to any one county — we work nationally — but our specialism is park home-specific, which is what matters more than postcode. The best park home inspector or quote reviewer in the country isn't likely to live in your village; what they need is park home expertise, not local familiarity.${examples ? `\n\nWe regularly help owners across ${examples} and the wider ${county} region.` : ""}`,
    },
    {
      h2: `Services we provide to ${county} residents`,
      body: `Independent quote review (most common): you send the quote in, we review the scope, cost and insurance relevance, you decide what to do next.\nInspection coordination: where the situation needs it, we arrange park-home-specialist independent inspections.\nInsurance claim guidance: independent input on scope, evidence and procedural steps — we don't handle the claim, we help you make better decisions.\nOngoing membership support: ask anything, anytime, for £99 first year (£249 thereafter).`,
    },
    {
      h2: `Common ${county} park home issues we see`,
      body: `Roof failures (covering, junctions, flashings) — the single most common quote category.\nDamp problems — usually a ventilation or junction issue rather than a "damp proof course" issue.\nBase level / drainage — particularly on older sites where ground conditions have shifted.\nStorm damage — fence, roof and tree-related damage that may or may not be insurable.\nDisputes over scope or pricing on quoted work.`,
    },
    {
      h2: `What to do next if you're in ${county}`,
      body: `If you've had a quote you're unsure about, send it in. There's no obligation, no contractor referral, no pressure. If you'd rather discuss the situation first, the contact form goes straight to us. Membership is optional — most first interactions are one-off quote reviews that don't require joining.`,
    },
  ],
  faq: [
    {
      q: `Are you based in ${county}?`,
      a: `We're a national service, not a local trader. What we offer — independent quote review, inspection coordination, insurance guidance — works equally well whether you're in ${county} or anywhere else in the UK. Park home expertise is the relevant qualification, not postcode.`,
    },
    {
      q: `Can you arrange a local inspector in ${county}?`,
      a: `Yes. When an inspection is the right next step, we coordinate with park-home-specialist inspectors with regional coverage. Members receive preferential rates.`,
    },
    {
      q: `Will you visit my park home?`,
      a: `Most quote reviews don't require a visit — photographs and written quotes are usually enough. Where a physical inspection is the right step, that's commissioned as a separate piece of work, with the inspector visiting on site.`,
    },
    {
      q: `What does it cost to use Park Home Support in ${county}?`,
      a: `Membership is £99 for the first year (£249/year afterwards). One-off quote review enquiries don't require membership.`,
    },
  ],
});

export const LOCATION_ESSEX = locationBody("Essex", "Colchester, Chelmsford, Clacton-on-Sea, Southend-on-Sea and Basildon");
export const LOCATION_KENT = locationBody("Kent", "Maidstone, Canterbury, Folkestone, Margate and Tunbridge Wells");
export const LOCATION_SUFFOLK = locationBody("Suffolk", "Ipswich, Bury St Edmunds, Lowestoft, Felixstowe and Sudbury");
export const LOCATION_NORFOLK = locationBody("Norfolk", "Norwich, King's Lynn, Great Yarmouth, Cromer and Thetford");
export const LOCATION_CAMBRIDGESHIRE = locationBody("Cambridgeshire", "Cambridge, Peterborough, Huntingdon, Wisbech and Ely");

// ─── KNOWLEDGE CENTRE ARTICLES ──────────────────────────────────────────────

export const KB_RESPONSIBILITY = {
  title: "Who Is Responsible for Repairs on a Park Home Site? | The Honest Answer",
  description:
    "Park home repair responsibility — the resident vs the site operator. What the Mobile Homes Act actually says, and where most disputes come from.",
  keywords: "park home repair responsibility, park operator responsibility, mobile homes act, who is responsible for park home repairs",
  canonical: "https://parkhomesupport.co.uk/who-is-responsible-for-park-home-repairs",
  eyebrow: "Knowledge Centre",
  h1: "Who Is Responsible for Repairs on a Park Home Site?",
  intro:
    "Repair responsibility on park home sites is one of the most misunderstood — and most disputed — areas in the whole sector. The simple version: the resident owns the home, the operator owns the site. Where each one's responsibility ends and the other's begins is exactly where disputes happen.",
  sections: [
    {
      h2: "The home itself",
      body:
        "The park home is the resident's property. Everything inside the cladding line, the roof, the windows, the doors, the chassis, the base (in most situations), the services into the home, and the home's general condition are the resident's responsibility to maintain.\n\nThat's true whether the home is owned outright, on finance, or held under a written agreement with the site.",
    },
    {
      h2: "The site / pitch",
      body:
        "The site operator is responsible for the pitch and the site infrastructure — roads, communal lighting, signage, communal drainage to the connection point, communal water supply, gas main to the pitch (where applicable), site fences, communal grounds and the overall safety of the site.\n\nThis is set out in the Mobile Homes Act 1983 (as amended) and in your written agreement.",
    },
    {
      h2: "Where most disputes happen",
      body:
        "1. Drainage — runoff from the pitch onto your base, or shared drainage that's failing.\n2. Trees and vegetation — overhanging branches damaging your home or roots affecting your base.\n3. Boundary fences — particularly where they affect your home or pitch.\n4. Site-wide infrastructure failures (electrical, water) that have damaged your home.\n5. Access — vehicles, deliveries, contractors.\n\nIn each of these, the resident's responsibility usually stops at the home and the operator's usually stops at the pitch — but causation matters. If something on the operator's side caused damage on the resident's side, the operator is on the hook for the consequences.",
    },
    {
      h2: "What to do when you're in dispute",
      body:
        "1. Document everything in writing — emails, dates, photographs.\n2. Refer to your written agreement and the Mobile Homes Act.\n3. Don't authorise work you think is the operator's responsibility without putting them on notice in writing first.\n4. If you've been pressured to authorise work you think isn't yours, send the situation in to Park Home Support before you sign.\n5. Where appropriate, the First-tier Tribunal (Property Chamber) is the route for unresolved Mobile Homes Act disputes — but most disputes are resolvable before that point.",
    },
  ],
  faq: [
    {
      q: "Can my site operator refuse to do repairs that are their responsibility?",
      a: "They can refuse, but if it's genuinely their responsibility under the agreement and the Mobile Homes Act, you have routes to compel — including the First-tier Tribunal. Document everything in writing.",
    },
    {
      q: "Is my park home base my responsibility or the operator's?",
      a: "Usually the resident's responsibility, but the pitch (ground around and underneath) is the operator's. Where ground conditions have caused base movement, the responsibility split depends on the specific facts.",
    },
    {
      q: "What if site infrastructure damages my home?",
      a: "Document the damage and the cause, notify the operator in writing immediately, get independent input before authorising any repair work yourself. Where the cause is genuinely on the operator's side, the cost should follow.",
    },
  ],
};

export const KB_OPERATOR_REFUSE = {
  title: "Can My Park Operator Refuse Repairs? | Your Rights Explained",
  description:
    "Park operator refused to do repairs? Understand what's actually their responsibility, what's yours, and the steps to resolve disputes properly.",
  keywords: "park operator refuse repairs, park operator responsibilities, mobile homes act disputes",
  canonical: "https://parkhomesupport.co.uk/can-park-operator-refuse-repairs",
  eyebrow: "Knowledge Centre",
  h1: "Can My Park Operator Refuse Repairs?",
  intro:
    "The short answer: a park operator can refuse to do things that aren't their responsibility. They can't refuse things that are. Knowing which is which — and how to enforce it — is the entire game.",
  sections: [
    {
      h2: "Things operators are responsible for",
      body:
        "Site roads and access, communal drainage up to the pitch connection, communal lighting, communal water supply, site-wide gas main where it exists, site fences (typically), communal grounds and grass cutting (where in the agreement), site signage and safety, and overall site management compliance.\n\nThey are not generally responsible for: anything inside your pitch boundary, your home, your services from the connection point, your steps, your skirting, or your driveway (unless specifically taken on in the agreement).",
    },
    {
      h2: "When a refusal is legitimate",
      body:
        "If you ask the operator to fix something that's clearly your responsibility — a damaged window, a leaking pipe inside the home, a soft floor — they're entirely within their rights to refuse. That's not the operator being awkward; that's the boundary of their responsibility.",
    },
    {
      h2: "When a refusal isn't legitimate",
      body:
        "If the issue is on their side and they're refusing — communal drainage flooding your pitch, site infrastructure damaging your home, fence collapse onto your home, an access issue affecting you — that's enforceable.\n\nThe escalation path: written notice citing the agreement and the Act, formal complaint following any documented process, the local authority where licence conditions are involved, and the First-tier Tribunal (Property Chamber) for unresolved Mobile Homes Act disputes.",
    },
    {
      h2: "Where Park Home Support helps",
      body:
        "We're not solicitors. What we can do is help you understand whether the situation is clearly yours, clearly theirs, or genuinely arguable — before you escalate. A lot of disputes resolve quickly once the facts are properly framed in writing.",
    },
  ],
  faq: [
    {
      q: "What can I do if my park operator refuses to do their share of repairs?",
      a: "Document the refusal in writing, refer to your agreement and the Mobile Homes Act, escalate via the operator's complaints procedure, involve the local authority if it's a licence issue, and use the First-tier Tribunal for unresolved disputes.",
    },
    {
      q: "Can I do the repairs myself and recover the cost?",
      a: "Only carefully. Self-help is risky without a clear legal basis. Get the situation reviewed and put the operator on formal notice first — otherwise you may end up paying twice.",
    },
    {
      q: "Is there a regulator for park operators?",
      a: "Site licensing is via the local authority. Behaviour can be raised with trading standards and (depending on the issue) the local authority's park homes officer. Disputes ultimately route via the First-tier Tribunal.",
    },
  ],
};

export const KB_CLAIM_REJECTED = {
  title: "Park Home Insurance Claim Rejected? | What To Do Next",
  description:
    "Park home insurance claim rejected — understand the common reasons, your options, and how independent input can strengthen an appeal.",
  keywords: "park home insurance claim rejected, park home insurance claim refused, park home insurance dispute",
  canonical: "https://parkhomesupport.co.uk/park-home-insurance-claim-rejected",
  eyebrow: "Knowledge Centre",
  h1: "Park Home Insurance Claim Rejected? Here's What To Do.",
  intro:
    "A claim rejection isn't the end of the road. It's often the beginning of a dispute that can be resolved with the right information. The first step is understanding the reason — properly — before responding.",
  sections: [
    {
      h2: "Common reasons claims get rejected",
      body:
        "Wear-and-tear (most common) — insurers don't cover gradual deterioration.\nLack of evidence — without before/after photographs and clear causation, claims fail.\nLapsed maintenance — particularly on roofs and seals.\nUndisclosed information at policy inception — material facts about the home not declared.\nNon-disclosure of prior claims or issues.\nIssue falling outside the wording (e.g. flood when the policy excludes it).\nClaim made too late.",
    },
    {
      h2: "Before you accept the rejection",
      body:
        "Read the reason carefully. Match it against the policy wording you actually hold. Photograph and document everything you can about the original event. Get the situation independently reviewed — sometimes what reads like a clean rejection is technically arguable.",
    },
    {
      h2: "The escalation path",
      body:
        "1. Your insurer's internal complaints process — give it the full statutory timeframe.\n2. Independent technical input — an inspection report, where appropriate, that supports your version of events.\n3. The Financial Ombudsman Service — free for the consumer, binding on the insurer if upheld. There's a strict deadline (currently six months from the insurer's final response) so don't sit on it.",
    },
    {
      h2: "Where independent support changes the outcome",
      body:
        "Many rejections rely on a contestable characterisation of the issue — \"wear and tear\" when the underlying cause was actually storm damage, for example. An independent inspection report can shift that conversation. We can review the situation and tell you whether an inspection is likely to help.",
    },
  ],
  faq: [
    {
      q: "Should I get a solicitor for a rejected park home claim?",
      a: "Not necessarily, and often not at first. The Financial Ombudsman is free and effective for most disputes. A solicitor is rarely needed unless the case is unusually large or complex.",
    },
    {
      q: "How long do I have to appeal?",
      a: "Six months from the insurer's final response to escalate to the Financial Ombudsman. Don't miss the deadline.",
    },
    {
      q: "Will an independent inspection help my appeal?",
      a: "Often, yes. Particularly where the rejection turns on the cause of the damage rather than the existence of damage.",
    },
  ],
};

export const KB_BASE_FAILURE = {
  title: "Park Home Base Failure Signs | What To Spot Early",
  description:
    "Park home base failure — the early signs, what they actually mean, and what to do before commissioning expensive base work.",
  keywords: "park home base failure, park home base relevel, park home base inspection",
  canonical: "https://parkhomesupport.co.uk/park-home-base-failure-signs",
  eyebrow: "Knowledge Centre",
  h1: "Park Home Base Failure Signs — Catch Them Early.",
  intro:
    "Base problems are amongst the most expensive park home repairs because they're often discovered late, after damage has been done above. Catching the early signs saves the most money — sometimes by a factor of five.",
  sections: [
    {
      h2: "The early indicators",
      body:
        "Doors that don't close cleanly — often a base movement clue before it's obvious anywhere else.\nWindows that stick or have visible gaps at corners.\nHairline cracks at interior corners of rooms.\nUneven floors detectable by walking — particularly across thresholds.\nAny new gap between the home and its skirting.\nWater pooling around the base in heavy rain.\nVisible movement at the chassis when you inspect the underside.",
    },
    {
      h2: "What these signs mean (and don't mean)",
      body:
        "Movement of half an inch over time on an old home is usually normal settlement and not an emergency. Acute new movement — over weeks, not years — is a different story and warrants a proper inspection.\n\nNot every uneven floor is a base failure. Sometimes it's a chassis cross-bracing issue, or a localised pad/pier issue rather than the whole base. Diagnosis matters because the price difference between a localised correction and a full relevel can be four-figure.",
    },
    {
      h2: "What proper diagnosis looks like",
      body:
        "An independent park home inspection covers: a level survey across the home, visual inspection of base and chassis, identification of root cause (soil, water, pad failure, chassis), and a recommended scope with options ranked by cost.\n\nAvoid: contractors who quote for a full relevel without diagnosing the cause; quotes that don't differentiate between the symptom and the root cause; pressure to start work \"before it gets worse\".",
    },
    {
      h2: "Common scope abuses",
      body:
        "Replacing all base materials when only some are failing.\nCorrecting symptoms (relevelling) without addressing causes (water ingress, soil movement).\nBundling roof or other work into a base quote.\nOpen-ended day rates rather than fixed scope pricing.",
    },
  ],
  faq: [
    {
      q: "How much does park home base repair cost?",
      a: "Localised work can be a few hundred pounds; full base/chassis interventions can be several thousand. The biggest variable is correct diagnosis — get that right and the price range narrows dramatically.",
    },
    {
      q: "How long does a park home base repair take?",
      a: "Localised work: 1–2 days. Full relevels: typically 3–7 days depending on access and scope.",
    },
    {
      q: "Does insurance cover park home base failure?",
      a: "Usually no — base movement over time is treated as wear-and-tear or ground movement that isn't typically covered. Some sudden events (subsidence after escape of water, for example) may be.",
    },
  ],
};

export const KB_MOBILE_HOMES_ACT = {
  title: "Understanding the Mobile Homes Act | Plain-English Guide",
  description:
    "The Mobile Homes Act 1983 in plain English — what it covers, what your written agreement should contain, and where your rights kick in.",
  keywords: "mobile homes act, mobile homes act 1983, park home written agreement, park home rights",
  canonical: "https://parkhomesupport.co.uk/understanding-mobile-homes-act",
  eyebrow: "Knowledge Centre",
  h1: "Understanding the Mobile Homes Act — Plain English.",
  intro:
    "The Mobile Homes Act 1983 (as amended in 2013) is the main piece of legislation governing residential park homes in England. It defines the relationship between the resident and the site operator and sets out what each side can and can't do.",
  sections: [
    {
      h2: "What the Act covers",
      body:
        "It applies to qualifying \"protected sites\" — residential park home sites licensed under the Caravan Sites and Control of Development Act 1960. It requires every resident to be given a written agreement covering the express terms of the pitch agreement.\n\nIt sets out the right to live on the pitch (security of tenure), the right to assign the agreement on sale, the implied terms that apply automatically regardless of what's written, and the procedural protections for residents.",
    },
    {
      h2: "Key implied terms (apply automatically)",
      body:
        "Quiet enjoyment of the home and pitch.\nReasonable site management.\nReasonable provision of services to the pitch.\nReasonable maintenance of communal areas, accesses and services.\nThe right to sell the home and assign the agreement to a buyer of your choosing (subject to the operator's reasonable approval).\nProtection against unfair pitch fee increases (RPI-linked formula with appeals to the Tribunal).",
    },
    {
      h2: "What your written agreement should contain",
      body:
        "The express terms of your pitch agreement, the pitch fee and how it's reviewed, services included and excluded, restrictions (animals, business use, additional structures), what happens on sale and inheritance, and any site-specific rules.\n\nIf you've never seen a written agreement, request one in writing. If terms in your written agreement contradict the implied terms in the Act, the Act wins.",
    },
    {
      h2: "Disputes — the Tribunal",
      body:
        "The First-tier Tribunal (Property Chamber) handles unresolved disputes — pitch fee reviews, breach of agreement, harassment claims, and questions about implied terms. Application is moderately accessible (small fee, no need for a solicitor in most cases) but takes time.",
    },
  ],
  faq: [
    {
      q: "Does the Mobile Homes Act apply to holiday park homes?",
      a: "No — it applies to residential pitches. Holiday pitches operate under different rules set out in their licences.",
    },
    {
      q: "Can my operator refuse a buyer when I sell?",
      a: "Only on limited grounds and only via the proper Tribunal-approved process. Blanket refusals or unreasonable conditions are challengeable.",
    },
    {
      q: "Is the pitch fee review capped?",
      a: "Pitch fee reviews follow a statutory presumption linked to RPI, with adjustments allowed for specified factors. If you don't agree, the matter goes to the Tribunal.",
    },
  ],
};

export const KB_DRAINAGE = {
  title: "Park Home Drainage Problems Explained | Causes & Solutions",
  description:
    "Park home drainage problems — what causes them, what's your responsibility vs the operator's, and what proper diagnosis looks like before any work is done.",
  keywords: "park home drainage, park home drainage problems, park home pitch drainage",
  canonical: "https://parkhomesupport.co.uk/park-home-drainage-problems",
  eyebrow: "Knowledge Centre",
  h1: "Park Home Drainage Problems Explained.",
  intro:
    "Drainage problems on park home sites are one of the most common causes of base damage, damp issues and disputes with operators. They're also one of the most diagnosable — if you know what to look for.",
  sections: [
    {
      h2: "The two layers of drainage",
      body:
        "Site drainage — the operator's responsibility. The communal drains, surface water management, and how rainfall moves across the site. If site drainage fails, water arrives at your pitch.\n\nPitch drainage — partly the operator's (the pitch itself), partly the resident's (immediate edges of the home, gutters, downpipes, surface treatments). If pitch drainage fails, water sits next to or under the home.",
    },
    {
      h2: "Symptoms and what they mean",
      body:
        "Pooling water around the home in rain — pitch drainage issue or surface gradient.\nDamp patches on internal walls near floor level — water ingress at base level.\nSoft ground around the home — long-running drainage problem starting to affect the substrate.\nUneven base over time — drainage causing differential ground movement.\nDamp under the home (when checked) — a sign that water is reaching the underside.\nFlooded driveways or paths to the pitch — site drainage problem.",
    },
    {
      h2: "What proper diagnosis covers",
      body:
        "A drainage assessment should look at: the gradient of the pitch, the condition of any french drains or surface drainage installed, the gutter and downpipe path, the connection of the home's drainage to the site main, the run-off pattern in heavy rain, and the wider site context above your pitch.\n\nWithout this, a contractor's solution often misses the cause. We've seen owners pay several thousand for relevelling when the underlying problem was a blocked communal drain feeding water onto the pitch — the relevelling will fail again within months.",
    },
    {
      h2: "Operator vs resident",
      body:
        "If runoff onto your pitch is coming from the site infrastructure or neighbouring pitches, that's the operator's issue and should be put to them in writing. If it's purely about gutters, downpipes or surface treatments within your pitch, that's typically yours.",
    },
  ],
  faq: [
    {
      q: "Who pays to fix drainage problems on a park home site?",
      a: "Depends on whether the cause is on the operator's side (site infrastructure) or the resident's side (pitch-specific). Proper diagnosis is the difference between paying yourself and being able to put the cost back to the operator.",
    },
    {
      q: "Can drainage problems cause base failure?",
      a: "Yes — long-running water ingress around or under a park home is one of the leading causes of base movement. Catching it early is dramatically cheaper than fixing the consequences.",
    },
    {
      q: "How much does park home drainage work cost?",
      a: "Localised drainage corrections can be a few hundred pounds; comprehensive solutions involving french drains or significant ground works can run into thousands. Diagnosis first, scope second, price third.",
    },
  ],
};

// ─── ADDITIONAL COMPETITOR ALTERNATIVE ──────────────────────────────────────

export const MAINTENANCE_PLAN_ALTERNATIVE = {
  title: "Park Home Maintenance Plan Alternative | Independent Support",
  description:
    "Looking at park home maintenance plans? Compare them with independent ongoing support — what each actually covers, and where the savings really come from.",
  keywords: "park home maintenance plan alternative, park home maintenance contract, park home support plan comparison",
  canonical: "https://parkhomesupport.co.uk/park-home-maintenance-plan-alternative",
  eyebrow: "Alternatives",
  h1: "Park Home Maintenance Plan Alternative — Pay For What You Use.",
  intro:
    "Park home maintenance plans typically bundle an annual visit, a small allowance for minor repairs and a phone line to call. They sound like good value until you look at what's actually included — and what isn't.",
  sections: [
    {
      h2: "What most maintenance plans actually contain",
      body:
        "Read the small print: most plans cover an annual visual check, minor sealant touch-ups, and a route into the provider's own contractor network when something larger comes up. Larger work is always quoted separately — and that's where the real money goes.\n\nThat means the value of a plan depends entirely on (a) the price of the annual visit standalone vs the plan, and (b) whether the contractor pricing on the bigger work is fair when it does arrive.",
    },
    {
      h2: "Where independent support is different",
      body:
        "Park Home Support doesn't bundle an annual visit, because most owners don't need one every year. Instead, the £99 first-year membership unlocks ongoing quote review, inspection coordination, insurance guidance and a place to send questions.\n\nWhen a contractor quotes you for actual work — roof, base, damp, anything — that quote gets independently reviewed before you commit. The savings on a single piece of properly-scrutinised work usually exceed several years of a maintenance plan.",
    },
    {
      h2: "Combining the two (if you want a visit)",
      body:
        "If you want an annual visit, commission an independent inspection rather than a maintenance plan tied to a contractor. Pay for it once, on its own terms, with no obligation to use the inspector for follow-up work. That keeps the relationship transparent.",
    },
  ],
  faq: [
    { q: "Are park home maintenance plans worth it?",
      a: "Sometimes, when the annual visit is genuinely needed and the in-plan price is materially below standalone. Often not, when the plan is mainly a route into a contractor network with separately-quoted work. Read the inclusions list carefully." },
    { q: "How is Park Home Support different from a maintenance plan?",
      a: "We're not a contractor. We don't carry out work. We review work that others are proposing to do, and give you independent guidance before you commit." },
    { q: "Can I cancel my maintenance plan and switch?",
      a: "Most plans have cancellation terms in the contract — typically you can cancel at the end of the term. Don't cancel without checking what you'd lose mid-year." },
  ],
};

// ─── SERVICE + LOCATION (TIGHTER, MORE INTENT-SPECIFIC THAN COUNTY PAGES) ───

const serviceLocation = ({ service, location, slug, intentKeywords, commonIssues, h1 }) => ({
  title: `${service} in ${location} | Park Home Support`,
  description: `${service} across ${location} — independent quote review, scope checks and inspection coordination before any work is approved. Plain-English help.`,
  keywords: intentKeywords,
  canonical: `https://parkhomesupport.co.uk/${slug}`,
  eyebrow: `${location}`,
  h1: h1 || `${service} in ${location}.`,
  intro: `Park home owners in ${location} contact Park Home Support for one reason: an independent view before money is committed. Whether you've been quoted for ${service.toLowerCase()} or you're trying to work out whether you actually need it, the steps are the same — and a short, structured review usually saves a lot of money.`,
  sections: [
    { h2: `What we cover for ${location} owners`,
      body: `${service} guidance for ${location} residents includes: independent review of the proposed scope and quote, comment on whether the work is necessary at all, identification of any insurance-relevant aspects, recommendation on whether a physical inspection is the right next step, and ongoing support throughout the work if you proceed.\n\nWe're not local to ${location} specifically — we operate nationally — but the questions we answer don't depend on postcode. They depend on park-home-specific expertise.` },
    { h2: `Common ${service.toLowerCase()} issues we see in ${location}`,
      body: commonIssues },
    { h2: "How the process works",
      body: "1. Send in the quote, the issue, photographs — whatever you've got.\n2. We review and come back with a plain-English written analysis: scope, price, insurance relevance, urgency, recommended next steps.\n3. You decide what to do. Push back on the contractor, request a proper inspection, approve the work, or pause.\n4. Optional membership covers ongoing support afterwards — quote reviews, follow-up questions, insurance input." },
    { h2: "Costs",
      body: "One-off quote review enquiries don't require membership — there's no upfront cost to send something in. Membership is £99 for the first year (then £249/year) for ongoing support. Inspections, when commissioned, are quoted separately and members receive preferential rates." },
  ],
  faq: [
    { q: `Are you based in ${location}?`,
      a: `We're a national specialist service, not a local trader. Park home expertise is what matters; postcode isn't. The same review process serves owners equally well across the UK.` },
    { q: `Can you arrange a local inspector for my ${location} park home?`,
      a: `Yes, where the situation needs it. We coordinate with park-home-specialist inspectors with regional coverage. Members receive preferential rates.` },
    { q: `How quickly can I get a quote reviewed?`,
      a: `Most reviews are returned within a few working days. Flag urgency on the form and we'll prioritise.` },
    { q: `What does this cost?`,
      a: `One-off enquiries: nothing upfront. Membership: £99 first year, £249/year thereafter.` },
  ],
});

export const INSPECTIONS_ESSEX = serviceLocation({
  service: "Park Home Inspections",
  location: "Essex",
  slug: "park-home-inspections-essex",
  intentKeywords: "park home inspections essex, park home survey essex, park home inspection colchester chelmsford",
  commonIssues: "Essex has a mix of long-established residential park home sites and newer developments along the coast. The most common inspection triggers we see for Essex owners: pre-purchase inspections (particularly along the Tendring coast and around Colchester), base level concerns on older inland sites, and insurance-related inspections after storm damage. Coastal exposure means roof, sealant and cladding condition tend to be the priority areas.",
  h1: "Park Home Inspections in Essex — Independent, Park-Home-Specific.",
});

export const REPAIRS_ESSEX = serviceLocation({
  service: "Park Home Repairs",
  location: "Essex",
  slug: "park-home-repairs-essex",
  intentKeywords: "park home repairs essex, park home repair quote essex, park home contractor essex",
  commonIssues: "Repairs we most often review for Essex owners: roof recoverings (felt or fibreglass) on older homes, base relevelling after winter movement, damp investigations on coastal-exposure homes, gutter and downpipe work after gutter failures, and storm-related fence and roof damage that may or may not be insurable. Quote review before approval is the single highest-leverage step.",
  h1: "Park Home Repairs in Essex — Get the Quote Checked First.",
});

export const SURVEYS_KENT = serviceLocation({
  service: "Park Home Surveys",
  location: "Kent",
  slug: "park-home-surveys-kent",
  intentKeywords: "park home survey kent, park home inspection kent, park home survey cost kent",
  commonIssues: "Kent's geology and weather drive the issues we see: ground movement on chalk-and-clay sites (particularly affecting bases), heavy rain-driven drainage issues, and coastal exposure on Romney Marsh / Thanet sites affecting roof and cladding longevity. Pre-purchase surveys are the most common reason Kent owners commission an independent inspection.",
});

export const REPAIRS_SUFFOLK = serviceLocation({
  service: "Park Home Repairs",
  location: "Suffolk",
  slug: "park-home-repairs-suffolk",
  intentKeywords: "park home repairs suffolk, park home contractor suffolk, park home repair quote suffolk",
  commonIssues: "Suffolk owners contact us most often about: roof repairs (particularly on older fibreglass roofs from the 1990s/2000s), base movement on softer ground sites inland, damp investigations where the cause is usually ventilation rather than ingress, and insulation/condensation issues common in older homes. Independent quote review applies the same way it does everywhere — get the price and scope sense-checked before approving.",
});

export const INSURANCE_HELP_NORFOLK = serviceLocation({
  service: "Park Home Insurance Help",
  location: "Norfolk",
  slug: "park-home-insurance-help-norfolk",
  intentKeywords: "park home insurance norfolk, park home insurance claim norfolk, park home flood norfolk",
  commonIssues: "Norfolk has higher-than-average claim activity due to flood, coastal exposure and storm. The most frequent insurance situations we provide independent input on: storm damage scope disputes, escape-of-water claims with damaged flooring and contents, refused or partially-paid claims on older homes, and confusion over whether base/ground issues are covered (usually not). We don't handle the claim — we give independent input on scope, evidence and the procedural steps.",
  h1: "Park Home Insurance Help in Norfolk — Independent Input on Claims.",
});

export const MAINTENANCE_CAMBRIDGESHIRE = serviceLocation({
  service: "Park Home Maintenance Support",
  location: "Cambridgeshire",
  slug: "park-home-maintenance-cambridgeshire",
  intentKeywords: "park home maintenance cambridgeshire, park home annual check cambridgeshire, park home maintenance plan cambridgeshire",
  commonIssues: "Cambridgeshire sites include both flat-fen settings and gentler upland sites; the maintenance issues differ accordingly. Fenland sites tend to flag drainage, base level and water-table effects. Upland sites focus more on roof, junctions and storm-driven issues. Either way, the cheapest maintenance is preventative — catching issues early before they need contractor intervention.",
});

// ─── CITY-LEVEL PAGES (TIGHTER STILL, ~500-700 WORDS) ───────────────────────

const cityPage = ({ city, county, service, slug, localContext }) => ({
  title: `${service} in ${city}, ${county} | Park Home Support`,
  description: `${service} guidance for ${city} park home owners — independent quote review, scope checks, inspection coordination. No contractor commission, no insurance tie-ins.`,
  keywords: `${service.toLowerCase()} ${city.toLowerCase()}, park home ${city.toLowerCase()}, park home support ${city.toLowerCase()}`,
  canonical: `https://parkhomesupport.co.uk/${slug}`,
  eyebrow: `${city}, ${county}`,
  h1: `${service} in ${city}.`,
  intro: `${city} and the surrounding ${county} area host a number of established park home sites. Park Home Support provides park home owners across ${city} with independent quote review, inspection coordination and insurance guidance — without contractor commission or insurance tie-ins.`,
  sections: [
    { h2: `${city} park home context`,
      body: localContext },
    { h2: "What we do for individual owners",
      body: `Independent quote review — you send a contractor's quote in, we review the scope, the cost and the insurance relevance before you commit to anything. Inspection coordination — where the situation warrants a physical inspection, we arrange a park-home-specialist inspector. Insurance input — independent comment on scope, evidence and procedural steps if a claim is involved. Ongoing support — membership-based if you want a place to send any future questions.\n\nWe don't appoint contractors and we don't accept commission. Decisions and contracts stay with you.` },
    { h2: "How to send something in",
      body: `Use the Quote Review form to send a written quote, a description of the issue and any photographs. Most reviews come back within a few working days. Urgency can be flagged on the form. There's no obligation to join membership to send a one-off enquiry.` },
  ],
  faq: [
    { q: `Are you based in ${city}?`,
      a: `No — we're a national specialist service. Park home expertise is the relevant qualification, not local presence. The same review process serves ${city} owners as well as any other UK location.` },
    { q: `Can you arrange an inspector to visit my ${city} park home?`,
      a: `Yes, when the situation warrants it. We coordinate park-home-specialist inspectors with regional coverage of ${county} and surrounding counties.` },
    { q: `Is this covered by membership or paid separately?`,
      a: `Quote review and one-off enquiries don't require membership. Ongoing support is membership-based (£99 first year, £249/year). Inspections are quoted separately; members receive preferential rates.` },
  ],
});

export const REPAIRS_COLCHESTER = cityPage({
  city: "Colchester",
  county: "Essex",
  service: "Park Home Repairs",
  slug: "park-home-repairs-colchester",
  localContext: "Colchester and the surrounding north Essex area has several long-established residential park home sites. The most common repair-quote situations we see for Colchester owners: roof recoverings on homes 20+ years old, base relevelling after winter, and damp investigations on the western (more exposed) side of homes. Quote review is the same process wherever you are — and saves the most money when applied before any work starts.",
});

export const SURVEYS_CHELMSFORD = cityPage({
  city: "Chelmsford",
  county: "Essex",
  service: "Park Home Surveys",
  slug: "park-home-surveys-chelmsford",
  localContext: "Chelmsford and the surrounding mid-Essex area is a mature park home market. Pre-purchase surveys are the most common reason owners and prospective buyers commission an independent inspection here — Essex's mix of older inland sites and newer developments means construction standards and condition vary significantly between sites, and a park-home-specialist inspection picks up what a generic chartered surveyor often misses.",
});

export const SUPPORT_CLACTON = cityPage({
  city: "Clacton-on-Sea",
  county: "Essex",
  service: "Park Home Support",
  slug: "park-home-support-clacton",
  localContext: "Clacton-on-Sea and the wider Tendring coast has one of the highest concentrations of park home sites in Essex. Coastal exposure is the dominant factor — roof, cladding, sealant and metalwork conditions all age faster on this coastline than further inland. The most common quote-review categories we see for Clacton residents are roof recoverings, exterior sealant work, and storm-damage repairs that may be partially insurable.",
});

// ─── INTENT-SPECIFIC PROBLEM PAGES ──────────────────────────────────────────

export const INSPECTION_COST = {
  title: "Park Home Inspection Cost UK | What You Actually Pay In 2026",
  description:
    "Park home inspection cost — what an independent inspection actually costs in the UK, what's included, and how to avoid paying for the wrong thing.",
  keywords: "park home inspection cost, park home survey cost, park home inspection price, park home inspection price uk",
  canonical: "https://parkhomesupport.co.uk/park-home-inspection-cost",
  eyebrow: "Costs",
  h1: "Park Home Inspection Cost — What You Actually Pay.",
  intro:
    "An independent park home inspection is one of the cheapest pieces of advice you can buy relative to what it prevents. This page explains what the typical cost range looks like in 2026, what should be included, and what to avoid paying extra for.",
  sections: [
    { h2: "Typical UK price range",
      body: "Independent park-home-specialist inspections in the UK typically range from £350 to £750 for a single home, depending on:\n\n• Scope (external only, or external + internal + base)\n• Location (travel time matters)\n• Reporting (basic vs photographically documented written report)\n• Specialism (general surveyor vs park-home specialist — the latter is worth more)\n\nMembers of Park Home Support receive preferential rates and priority access; the standalone cost is similar to commissioning directly." },
    { h2: "What should be included",
      body: "A useful inspection includes: external condition (cladding, joints, vents), roof (covering, junctions, flashings, gutters), windows and doors, base (level survey, condition, drainage), chassis and underside (where accessible), interior (damp, movement, soft spots), services (gas certificate currency, visible electrical), and a written report with photographs, severity ratings and recommended actions.\n\nIf any of those elements are excluded or charged extra, ask why upfront." },
    { h2: "What to avoid paying for",
      body: "Bundled work — a quoted 'inspection' that's really a sales visit to quote for work afterwards. The inspector should have no financial stake in the work that follows.\n\nGeneric house surveys — RICS HomeBuyer Reports on park homes regularly miss the issues that actually matter (base, chassis, vapour barrier, ventilation patterns).\n\nVerbal-only reports — if it isn't documented in writing with photographs, it can't be used in dispute, insurance correspondence, or a sale.\n\nUpfront 'membership fees' from inspector networks that route work to their own contractors." },
    { h2: "When is an inspection worth it?",
      body: "Before any major work — even a £600 inspection on a £4,000 roof quote pays for itself if the inspection prevents one bad scope decision.\n\nBefore purchasing — yes, every time, on top of any conveyancing survey.\n\nBefore selling — once, to know what a buyer's inspector will find.\n\nAfter an insurance event — to document the situation independently before committing to repair.\n\nIn dispute — independent inspection is often the missing piece in disputes with operators, contractors or insurers." },
  ],
  faq: [
    { q: "Is a park home inspection the same as a house survey?",
      a: "No. Park homes have their own construction, failure patterns and regulatory context. Generic surveyors regularly miss what matters. Always pick park-home-specialist." },
    { q: "How long does an inspection take?",
      a: "On-site, typically 2–4 hours for a full external + internal + base inspection. Reports usually arrive within a few working days." },
    { q: "Will Park Home Support do the inspection itself?",
      a: "We coordinate with park-home-specialist inspectors rather than doing the inspection ourselves. That keeps our review of your quote independent of any potential follow-up work." },
    { q: "Can I get a cheaper inspection?",
      a: "Yes — but read carefully what's actually included. A £200 'inspection' that's really a sales visit isn't comparable to a documented £500 independent inspection. Compare the report, not just the price." },
  ],
};

export const UNDERFLOOR_INSULATION = {
  title: "Park Home Underfloor Insulation Problems | Causes & Solutions",
  description:
    "Park home underfloor insulation problems — cold floors, damp underside, vapour barrier failure. Independent guidance before you commission expensive replacement work.",
  keywords: "park home underfloor insulation, park home insulation problem, park home cold floor, park home vapour barrier",
  canonical: "https://parkhomesupport.co.uk/park-home-underfloor-insulation-problem",
  eyebrow: "Problem Solving",
  h1: "Park Home Underfloor Insulation Problems.",
  intro:
    "Cold floors and damp underside conditions are common in older park homes. The fix isn't always 'replace all the insulation' — and the wrong scope, quoted aggressively, is one of the more common ways park home owners over-spend on remediation that doesn't address the real cause.",
  sections: [
    { h2: "The three problems that get conflated",
      body: "1. Missing or compressed insulation — material no longer doing its job thermally.\n2. Wet insulation — material that's absorbed water from below.\n3. Failed vapour barrier — the membrane between the home's floor and the insulation that should stop interior moisture migrating downwards. When this fails, even new insulation will wet itself from inside the home.\n\nThese have three different causes and three different fixes. Conflating them — particularly fixing #1 when the real issue is #3 — is expensive and short-lived." },
    { h2: "Symptoms and what they suggest",
      body: "Cold floors only in certain rooms: localised insulation compression or rodent damage.\n\nCold floors everywhere: full underfloor insulation degradation, or a vapour barrier that's failed and wet the insulation throughout.\n\nDamp patches near floor edges internally: water ingress at the base/skirting, not necessarily an insulation issue at all.\n\nVisible insulation falling from underside (when you look): physical detachment — almost always replace.\n\nMusty smell near skirtings: usually vapour barrier or ventilation issue, sometimes both." },
    { h2: "Proper diagnosis before scope",
      body: "A good independent inspection looks at: condition of the insulation under the home, condition and continuity of the vapour barrier (where visible), condition of the underside membrane, evidence of water ingress at base / skirting level, ventilation grilles around the home (often blocked or insufficient), and the internal floor for cold spots and dampness.\n\nWithout this, the typical contractor scope is 'replace all the insulation' — which sometimes is right, but quite often is treating the symptom rather than the cause." },
    { h2: "Cost ranges and what to expect",
      body: "Standalone underside insulation replacement: typically £1,200 — £3,500 depending on home size and access. Replacement with vapour barrier overhaul: £2,500 — £5,500. Combined with skirting / drainage corrections: more.\n\nGet any insulation quote reviewed before committing. The single most useful question to ask the contractor: 'how do you know it's an insulation problem and not a vapour barrier or ventilation problem?' If they can't answer that, ask for proper diagnosis first." },
  ],
  faq: [
    { q: "How long should park home underfloor insulation last?",
      a: "20+ years if the vapour barrier is intact and there's no water ingress. Significantly less if either fails." },
    { q: "Can I add more insulation under my park home myself?",
      a: "Possibly, depending on access and the existing setup. Worth getting it inspected first — adding insulation over a failing vapour barrier or wet existing material will make things worse, not better." },
    { q: "Is underfloor insulation work covered by insurance?",
      a: "Generally no — it's classed as wear and maintenance. Some related water-ingress damage may be covered if there was a sudden insurable event behind it." },
    { q: "Will Park Home Support review my insulation quote?",
      a: "Yes. Send the quote and the contractor's diagnosis through — we'll comment on whether the scope addresses the actual cause." },
  ],
};
